import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { IUBENDA_PRIVACY_URL } from '../components/legal/iubenda';
import { ChevronRight, ChevronDown, FileText, RotateCcw, BookOpen, HelpCircle, Download, Search, Star, BarChart3, ShoppingCart, Package, ArrowRight, AlertCircle, Bell } from 'lucide-react';

interface NavItemDef {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItemDef[] = [
  { id: 'risorse', title: 'Risorse & Modulistica', icon: FileText },
  { id: 'resi', title: 'Reso Prodotti', icon: RotateCcw },
  { id: 'catalogo-online', title: 'Il Catalogo Online', icon: BookOpen },
  { id: 'faq', title: 'FAQ', icon: HelpCircle },
];

function Risorse() {
  const docs = [
    { name: 'Informativa sulla Privacy', desc: 'Trattamento dei dati personali', format: 'Pagina web', size: '-', href: IUBENDA_PRIVACY_URL },
    { name: 'Condizioni di Vendita', desc: 'Termini e condizioni generali', format: 'Pagina web', size: '-', to: '/legale/condizioni' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Risorse & Modulistica</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          DTK offre ai propri clienti un portale completo per gestire ogni fase del rapporto commerciale: dalla registrazione online, alla ricerca prodotti, all'acquisto, alla spedizione, fino alla gestione dei resi e delle segnalazioni, tutto direttamente dalla tua Area Clienti.
        </p>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4">Documenti Scaricabili</h2>
        <div className="space-y-2">
          {docs.map(d => {
            const content = (<>
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium group-hover:text-[hsl(214,58%,41%)]">{d.name}</div>
                <div className="text-xs text-muted-foreground">{d.desc}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs font-medium">{d.format}</div>
                {d.size !== '-' && <div className="text-[10px] text-muted-foreground">{d.size}</div>}
              </div>
            </>);
            const cls = 'flex items-center gap-4 px-4 py-3.5 rounded-xl border bg-white hover:border-[hsl(var(--dtk-focus))] transition-colors duration-150 cursor-pointer group';
            return d.href
              ? <a key={d.name} href={d.href} target="_blank" rel="noopener noreferrer" className={cls}>{content}</a>
              : <Link key={d.name} to={d.to ?? '/legale'} className={cls}>{content}</Link>;
          })}
        </div>
      </div>
    </div>
  );
}

function Resi() {
  const steps = [
    { num: '1', title: 'Apri la richiesta online', desc: 'Puoi procedere cercando il prodotto per codice o nome dalla sezione "Cerca per prodotto". Scegli gli articoli e le quantità da rendere.' },
    { num: '2', title: 'Indica la motivazione', desc: "Per ogni pezzo seleziona il motivo del reso (difetto, articolo errato, qualità non conforme, ecc.) e aggiungi una descrizione con eventuali foto richieste." },
    { num: '3', title: 'Attendi autorizzazione', desc: "Il team DTK valuterà la richiesta entro 5 giorni lavorativi. Riceverai una notifica con l'esito nell'Area Avvisi." },
    { num: '4', title: 'Prepara e spedisci', desc: "Dopo l'approvazione, imballa l'articolo in un adeguato imballo esterno e spedisci in porto franco entro 15 giorni, salvo diversi accordi con il team di supporto DTK." },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Reso Prodotti</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          La procedura di reso è completamente digitale: puoi aprire una richiesta RMA direttamente dall'
          <Link to="/area-clienti/rma/cerca" className="text-[hsl(214,58%,41%)] font-medium hover:underline">
            area clienti
          </Link>
          {' '}del portale.
        </p>
      </div>

      <Link
        to="/area-clienti/rma/cerca"
        data-testid="reso-cerca-card"
        className="flex items-center gap-3 rounded-xl border bg-white p-5 hover:border-[hsl(var(--dtk-focus))] hover:bg-[hsl(var(--dtk-canvas))]/40 transition-all group"
      >
        <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
          <Search className="w-5 h-5 text-[hsl(214,58%,41%)]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold group-hover:text-[hsl(214,58%,41%)] transition-colors">Cerca per codice o nome del prodotto</div>
          <div className="text-xs text-muted-foreground">Trova l'articolo che vuoi rendere e avvia la richiesta di reso</div>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      </Link>

      <div>
        <h2 className="text-lg font-semibold mb-4">Procedura di Reso</h2>
        <div className="space-y-3">
          {steps.map(s => (
            <div key={s.num} className="flex items-start gap-4 p-4 rounded-xl border bg-white">
              <div className="w-8 h-8 rounded-full bg-[hsl(214,58%,41%)] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                {s.num}
              </div>
              <div>
                <div className="text-sm font-semibold">{s.title}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border bg-amber-50 border-amber-200 p-5">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">Nota Importante</h3>
            <ul className="text-sm text-amber-700 mt-2 space-y-1">
              <li>{"• La garanzia sui prodotti è di 1 anno (se non specificato diversamente)"}</li>
              <li>{"• Non viene accettato materiale trascorso 1 anno dalla fatturazione"}</li>
              <li>{"• La merce deve essere accompagnata da bolla di accompagnamento"}</li>
              <li>{"• Gli articoli con confezioni danneggiate o toner consumato oltre il 30% non saranno accreditati"}</li>
              <li>{"• Puoi monitorare lo stato della tua pratica RMA nell'Area Clienti > RMA"}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function CatalogoOnline() {
  const features = [
    { icon: Search, title: 'Ricerca Rapida e Avanzata', desc: 'Cerca per marca, codice OEM, modello stampante. Ricerca avanzata con 12 filtri combinabili.' },
    { icon: BarChart3, title: 'Disponibilità in Tempo Reale', desc: "Verifica subito la disponibilità a magazzino di ogni articolo, con arrivi previsti per i prodotti esauriti." },
    { icon: Star, title: 'Preferiti e Parco Macchine', desc: 'Salva i prodotti che ordini di più e le tue stampanti per trovare al volo i consumabili compatibili.' },
    { icon: ShoppingCart, title: 'Carrello Intelligente', desc: "Aggiungi prodotti con un click, modifica le quantità, gestisci disponibilità parziali e salva bozze d'ordine." },
    { icon: Package, title: 'Storico Ordini e Documenti', desc: 'Consulta ordini, fatture, DDT, note di credito. Riacquista con un click e monitora lo stato delle spedizioni.' },
    { icon: Bell, title: 'Avvisi Stock', desc: "Aggiungi un numero illimitato di avvisi stock e ricevi una notifica mail non appena un articolo torna disponibile." },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Il Catalogo Online</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          Il portale DTK è il tuo strumento completo per gestire approvvigionamenti in modo rapido e intelligente. Cerca tra migliaia di prodotti, confronta, ordina e gestisci resi — tutto in un unico punto.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {features.map(f => (
          <div key={f.title} className="rounded-xl border bg-white p-5 hover:border-[hsl(var(--dtk-focus))] transition-colors">
            <div className="w-10 h-10 rounded-lg bg-[hsl(210,40%,93%)] flex items-center justify-center mb-3">
              <f.icon className="w-5 h-5 text-[hsl(214,58%,41%)]" />
            </div>
            <h3 className="text-sm font-semibold mb-1">{f.title}</h3>
            <p className="text-sm text-muted-foreground">{f.desc}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-[hsl(214,58%,41%)] text-white p-6 sm:p-8">
        <h2 className="text-lg font-semibold mb-2">Accedi al Portale</h2>
        <p className="text-sm opacity-80 mb-4">Il portale è riservato a Distributori e Rivenditori. Registrati per accedere a prezzi riservati, disponibilità in tempo reale, gestione ordini e resi digitali.</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/accedi" className="h-10 px-5 rounded-lg bg-white text-[hsl(214,58%,41%)] text-sm font-medium flex items-center gap-2 hover:bg-white/90 transition-colors">
            Accedi <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/registrati" className="h-10 px-5 rounded-lg border border-white/30 text-sm font-medium flex items-center gap-2 hover:bg-white/10 transition-colors">
            Registrati
          </Link>
        </div>
      </div>
    </div>
  );
}

function FAQ(): React.ReactElement {
  const [openIdx, setOpenIdx] = useState<string | null>(null);

  const categories = [
    {
      id: 'consegna',
      label: 'Consegna della merce',
      faqs: [
        {
          q: 'A quale corriere affidate le spedizioni?',
          a: 'La merce normalmente viene spedita tramite nostri corrieri convenzionati BRT · GLS · TNT, ma è sempre possibile fare richiesta di un corriere di Vostra fiducia al nostro customer service.',
        },
        {
          q: 'È possibile avere la merce in fermo deposito?',
          a: 'Sì, nella pagina web del carrello è possibile inserire un indirizzo di spedizione da denominare "FERMO DEPOSITO" e il nome del corriere prescelto.',
        },
        {
          q: 'Posso inserire un indirizzo specifico di destinazione della merce?',
          a: 'Sì, nella pagina web del carrello è possibile inserire più indirizzi diversi da quello predefinito.',
        },
        {
          q: 'Fate spedizioni all\'estero?',
          a: 'Sì, DTK può ricevere ordini per merce da consegnare all\'estero.',
        },
        {
          q: 'Gestite il dropshipping?',
          a: 'Sì, ai nostri clienti offriamo gratuitamente il servizio di dropshipping. Il pacco viaggerà senza documenti e con nastro neutro senza alcun riferimento.',
        },
        {
          q: 'Cosa devo verificare al momento della consegna della merce?',
          a: 'I pacchi sono sigillati sempre con nastro DTK. Eventuali danni visibili esteriori al pacco o manomissione del nastro dovranno essere immediatamente contestati al corriere apponendo firma con riserva di controllo. Tale informazione ci deve essere comunicata entro 5 giorni dalla ricezione della merce a mezzo mail all\'indirizzo assistenza@dtksrl.eu, fornendo in oggetto il numero di spedizione e nel corpo mail i Vostri riferimenti con descrizione di quanto accaduto. Senza l\'apposizione di riserva specifica il pacco risulta accettato e di conseguenza non potrà essere rimborsato.',
        },
      ],
    },
    {
      id: 'pagamenti',
      label: 'Pagamenti',
      faqs: [
        {
          q: 'A chi devo intestare l\'assegno?',
          a: 'L\'assegno che va dato al corriere deve essere intestato esclusivamente a DTK S.r.l.',
        },
        {
          q: 'Quali sono le coordinate per effettuare il bonifico bancario?',
          a: 'DTK S.r.l. — UNICREDIT S.P.A., Agenzia Via delle Montagne Rocciose\nIBAN: IT 19G 02008 05198 000 102277024',
        },
        {
          q: 'È possibile avere condizioni di pagamento diverse da Contrassegno/Bonifico anticipato?',
          a: 'Sì, dopo i primi ordini è possibile fare richiesta al nostro ufficio amministrativo per concordare, se possibile, una forma di pagamento dilazionata.',
        },
      ],
    },
    {
      id: 'ordini',
      label: 'Ordini',
      faqs: [
        {
          q: 'Esiste un minimo d\'ordine?',
          a: 'Sì, il nostro minimo d\'ordine è di €50,00 di imponibile merce. Per ordini inferiori a tale valore verranno addebitati €3,00 come contributo di imballaggio.',
        },
        {
          q: 'Posso restituire merce ordinata per errore?',
          a: 'Sì, è possibile restituire la merce con spedizione a noi indirizzata in porto franco entro 30 giorni dall\'acquisto, nel suo imballo originale e previa autorizzazione da parte di DTK S.r.l. Verrà applicata una franchigia del 5% se restituita entro il primo mese e del 10% per ogni mese successivo.',
        },
        {
          q: 'Posso verificare lo stato di avanzamento del mio ordine?',
          a: 'È possibile controllare lo stato di avanzamento dell\'ordine nell\'apposita sezione "Ordini" dell\'area personale.',
        },
      ],
    },
    {
      id: 'garanzia',
      label: 'Garanzia sui prodotti',
      faqs: [
        {
          q: 'La fattura vale come garanzia?',
          a: 'Sì, la fattura è un documento valido per l\'esercizio della garanzia. I prodotti sono coperti dalla garanzia convenzionale offerta dal produttore. Per maggiori informazioni si rimanda alle Condizioni di Vendita.',
        },
      ],
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Domande Frequenti</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          Risposte alle domande più comuni su spedizioni, pagamenti, ordini e garanzie.
        </p>
      </div>

      <div className="space-y-8" data-testid="faq-accordion">
        {categories.map(cat => (
          <div key={cat.id}>
            {/* Intestazione categoria */}
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-sm font-bold uppercase tracking-widest text-[#2B5EA7]">{cat.label}</h2>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="space-y-2">
              {cat.faqs.map((faq, i) => {
                const key = `${cat.id}-${i}`;
                const isOpen = openIdx === key;
                return (
                  <div key={key} className="rounded-xl border bg-white overflow-hidden">
                    <button
                      onClick={() => setOpenIdx(isOpen ? null : key)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary/30 transition-colors"
                      data-testid={`faq-item-${key}`}
                    >
                      <span className="text-sm font-medium pr-4">{faq.q}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4 border-t bg-[hsl(var(--dtk-canvas))]">
                        <p className="text-sm text-muted-foreground leading-relaxed pt-3 whitespace-pre-line">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-[hsl(var(--dtk-canvas))] p-6 text-center">
        <HelpCircle className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-sm font-semibold mb-1">Non hai trovato la risposta?</h3>
        <p className="text-xs text-muted-foreground mb-4">Il nostro team è a tua disposizione per qualsiasi domanda.</p>
        <div className="flex items-center justify-center gap-4">
          <Link to="/contatti" className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-2 hover:bg-[hsl(214,58%,46%)] transition-colors">
            Contattaci
          </Link>
          <span className="text-sm text-muted-foreground">oppure chiama il <strong>06/5135753</strong></span>
        </div>
      </div>
    </div>
  );
}

const PAGE_COMPONENTS = {
  'risorse': Risorse,
  'resi': Resi,
  'catalogo-online': CatalogoOnline,
  'faq': FAQ,
};

export default function SupportPage() {
  const { pageId } = useParams();
  if (!pageId) return <Navigate to="/supporto/risorse" replace />;

  const PageComponent = PAGE_COMPONENTS[pageId];

  if (!PageComponent) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h1 className="text-2xl font-semibold">Pagina non trovata</h1>
        <Link to="/" className="text-sm text-[hsl(var(--dtk-focus))] mt-4 inline-block hover:underline">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div data-testid="support-page">
      <div className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span>Supporto</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground font-medium">{NAV_ITEMS.find(n => n.id === pageId)?.title}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* order: su mobile la navigazione di sezione deve seguire il
              contenuto, altrimenti il titolo della pagina finisce dopo
              ~430px di menu. Da lg in su l'ordine torna quello del DOM. */}
          <aside className="order-2 lg:order-none lg:w-56 flex-shrink-0">
            <div className="rounded-xl border bg-white p-4 sticky top-20">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Assistenza</h3>
              <div className="space-y-1">
                {NAV_ITEMS.map(p => (
                  <Link key={p.id} to={`/supporto/${p.id}`}
                    className={`tap flex items-center gap-2 px-3 py-2.5 lg:py-2 rounded-lg text-sm transition-colors duration-150 ${
                      p.id === pageId ? 'bg-[hsl(210,40%,93%)] text-[hsl(214,58%,41%)] font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                    }`}
                  >
                    <p.icon className="w-4 h-4 flex-shrink-0" />
                    {p.title}
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <div className="order-1 lg:order-none flex-1 min-w-0">
            <div className="rounded-xl border bg-white p-6 sm:p-8">
              <PageComponent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
